import { IDeepCopy, IToString } from '@common/interfaces';
import moment, { type Moment } from 'moment-timezone';

export class DateTime implements IDeepCopy<DateTime>, IToString {
    public date: Date = null;
    timeZone?: TimeZone;

    private static _dateFormat: string = null;
    public static get dateFormat(): string {
        if (!DateTime._dateFormat) {
            DateTime._dateFormat = 'MM/DD/YYYY';

            const _p = new Date(99, 11, 31, 15, 15, 15, 0);
            let explodeChar = '';

            const _dt = _p.toLocaleDateString();
            if (_dt.indexOf('.') > -1) explodeChar = '.';
            if (_dt.indexOf('/') > -1) explodeChar = '/';

            if (explodeChar !== '') {
                const arrDt = _dt.split(explodeChar);
                if (arrDt[0] > arrDt[1]) {
                    DateTime._dateFormat = `DD${explodeChar}MM${explodeChar}YYYY`;
                } else {
                    DateTime._dateFormat = `MM${explodeChar}DD${explodeChar}YYYY`;
                }
            }
        }
        return DateTime._dateFormat;
    }

    private static _dateTimeFormat: string = null;
    public static get dateTimeFormat(): string {
        if (!DateTime._dateTimeFormat) {
            DateTime._dateTimeFormat = 'MM/DD/YYYY HH:mm:ss';

            const _p = new Date(99, 11, 31, 15, 15, 15, 0);
            let explodeChar = '';

            const _dt = _p.toLocaleDateString();
            if (_dt.indexOf('.') > -1) explodeChar = '.';
            if (_dt.indexOf('/') > -1) explodeChar = '/';

            if (explodeChar !== '') {
                const arrDt = _dt.split(explodeChar);
                if (arrDt[0] > arrDt[1]) {
                    DateTime._dateTimeFormat = `DD${explodeChar}MM${explodeChar}YYYY ` + 'HH:mm:ss';
                } else {
                    DateTime._dateTimeFormat = `MM${explodeChar}DD${explodeChar}YYYY ` + 'HH:mm:ss';
                }
            }
        }
        return DateTime._dateTimeFormat;
    }

    public get(): Date {
        if (typeof this.date === 'string') {
            const momentDate = moment(this.date);
            if (momentDate.isValid()) {
                this.date = momentDate.toDate();
            }
        }
        return this.date;
    }

    public set(value: Date) {
        if (value) {
            this.date = value;
            this._moment = moment(value);
        } else {
            this.date = null;
            this._moment = null;
        }
    }

    constructor(date?: string | Date) {
        if (date) {
            const momentDate = moment(date);
            if (momentDate.isValid()) {
                this.date = momentDate.toDate();
                this._moment = momentDate;
            }
        }
    }

    private _moment: Moment = null;
    get moment(): Moment {
        if (this.date?.getUTCFullYear() === 1800) return null;
        return this._moment;
    }

    // public moment(): any {
    //     if (this.date) {
    //         return moment(this.date);
    //     }
    //     return null;
    // }

    public toDateString(): string {
        if (this.date?.getUTCFullYear() === 1800) return '';
        return DateTime.formatDate(this.date);
    }

    public static formatDate(d: Date): string {
        return d ? moment(d).format(DateTime.dateFormat) : '';
    }

    public toDateTimeString(): string {
        if (this.date?.getUTCFullYear() === 1800) return '';
        return DateTime.formatMomentToLocalDateTimeString(moment(this.date), true);
    }

    public toISODateTimeString(): string {
        return this.date ? moment(this.date).format('YYYY-MM-DD HH:mm:ss') : '';
    }

    public toShortISODateTimeString(): string {
        return this.date ? moment(this.date).format('YYYY-MM-DD hh:mm A') : '';
    }

    public static formatDateTime(d: Date): string {
        return d ? moment(d).format(DateTime.dateTimeFormat) : '';
    }

    public static formatMomentToLocalDateTimeString(date: moment.Moment, includeSeconds?: boolean): string {
        const timeFormat = includeSeconds ? 'LTS' : 'LT';
        return date.format(`${moment?.localeData().longDateFormat('L')} ${moment?.localeData().longDateFormat(timeFormat)}`);
    }

    public static formatMomentToLocalDateString(date: moment.Moment): string {
        return date.format(`${moment?.localeData().longDateFormat('L')}`);
    }

    public equals(target: DateTime): boolean {
        if (target && (<any>target).indexOf) {
            target = new DateTime();
            target.set(new Date(<any>target));
        }
        if (!target) {
            return false;
        }
        if (!this.get()) {
            return !target.get();
        }
        if (!target.get()) {
            return false;
        }
        return this.get().getTime() === target.get().getTime();
    }

    public fromJSON(json: string): DateTime {
        const ret = JSON.parse(json);
        return new DateTime(ret.date);
    }

    public static convertToTargetTZ(d: Date, sourceTimeZoneId: string, targetTimeZoneId: string) {
        if (!d) {
            d = new Date();
        }

        const date = new DateTime(d);
        if (date?.moment?.isValid()) {
            const dateTimeString = date.moment.format('YYYY-MM-DD HH:mm');
            const sourceZonedDate = moment.tz(dateTimeString, sourceTimeZoneId);
            const targetZonedDate = moment.tz(dateTimeString, targetTimeZoneId);
            const minutesOffset = targetZonedDate.utcOffset() - sourceZonedDate.utcOffset();
            const newDate = new Date(date.get().setMinutes(date.get().getMinutes() + minutesOffset));
            return newDate;
        }
        return d;
    }

    public static handleDate(dateVal: Date): string {
        const year = dateVal.getFullYear();
        const month = dateVal.getMonth() + 1;
        const monthNum = month < 10 ? `0${month}` : month;
        const date = dateVal.getDate();
        const dateNum = date < 10 ? `0${date}` : date;
        return `${year}-${monthNum}-${dateNum}`;
    }

    public static isValidDate(dateString: string) {
        const newDate = moment(dateString, DateTime.dateFormat, true);
        return newDate.isValid();
    }

    public deepCopy(): DateTime {
        const ret = new DateTime(this.toISODateTimeString());
        return ret;
    }

    public isNewer(d: DateTime): boolean {
        if (!this.date && d) return false;
        if (this.date && !d) return true;
        const currentDate: Date = this.get();
        const inDate: Date = d.get();
        return currentDate.getTime() > inDate.getTime();
    }

    public isBlank(): boolean {
        if (this.date === null) return true;
        if (this.date.getUTCFullYear() === 1800) return true;
        return false;
    }

    public toString(): string {
        return this.toDateString();
    }

    public fromNow(): string {
        return moment(this.get()).fromNow();
    }
}
